import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DynamicComponent, DynamicIoDirective } from 'ng-dynamic-component';
import { StoryblokComponentInstance } from '../../../interfaces/storyblok';
import { StoryblokLoadComponentsService } from '../../../services/storyblok-load-components.service';
import { BlockStyle } from '../../interfaces/block-style';
import { BlockWrapperComponent } from '../block-wrapper/block-wrapper.component';

@Component({
  selector: 'app-content-group',
  imports: [DynamicComponent, DynamicIoDirective, BlockWrapperComponent],
  templateUrl: './content-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentGroupComponent {
  private storyblokLoadComponentsService = inject(StoryblokLoadComponentsService);
  readonly _uid = input<string>(undefined); // just here to prevent errors
  readonly component = input<string>(undefined); // just here to prevent errors
  readonly _editable = input<string>(undefined);
  readonly style = input<BlockStyle[]>(undefined);
  readonly blocks = input<StoryblokComponentInstance[]>(undefined);
  readonly alignBlocks = input<string>(undefined);
  readonly extraDataMap = input<Map<string, { [key: string]: unknown }>>(undefined);

  components = this.storyblokLoadComponentsService.components;

  readonly resolvedStyle = computed(() => contentGroupStyle(this.style(), this.alignBlocks()));

  readonly renderedBlocks = computed(() => renderedColumn(this.blocks(), this.extraDataMap()));
}

function contentGroupStyle(style: BlockStyle[] | undefined, alignBlocks: string | undefined): BlockStyle[] {
  const source = style ?? [];
  const first: BlockStyle = {
    ...(source[0] ?? ({} as BlockStyle)),
    alignLeftChildPages: alignBlocks === 'left',
  };
  return [first, ...source.slice(1)];
}

function renderedColumn(
  items: StoryblokComponentInstance[] | undefined,
  extraDataMap: Map<string, { [key: string]: unknown }> | undefined
) {
  return (items ?? []).map(blok => renderedBlok(blok, extraDataMap));
}

function renderedBlok(
  blok: StoryblokComponentInstance,
  extraDataMap: Map<string, { [key: string]: unknown }> | undefined
) {
  const resolved = withInWrapper(blok);
  const inputs: Record<string, unknown> = { ...resolved };
  const componentName = resolved.component as string;
  const extraData = extraDataMap?.get(componentName);
  if (extraData) {
    for (const key of Object.keys(extraData)) inputs[key] = extraData[key];
  }
  return {
    uid: resolved._uid as string,
    component: componentName,
    inputs,
  };
}

function withInWrapper<T extends { style?: BlockStyle[] }>(item: T): T {
  if (!item || !('style' in item) || !item.style) return item;
  const style = item.style;
  if (!Array.isArray(style)) return item;
  const nextStyle: BlockStyle[] =
    style.length === 0 ? [{ inWrapper: true } as BlockStyle] : [{ ...style[0], inWrapper: true }, ...style.slice(1)];
  return { ...item, style: nextStyle };
}
